import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Full-screen application boot / cold-start loader.
 *
 * Implements §4.1 of the Promang Loading & Motion System
 * (promang-docs/public/docs/core/PROMANG_LOADING_SYSTEM.md):
 * grid background + ambient glow, a layered animated mark (slow dashed orbit +
 * fast conic gradient ring + floating gradient tile with an inner spinner),
 * the PROMANG wordmark, a rotating status message and a thin gradient progress
 * bar with a "% / secure session" caption.
 *
 * Tokens + keyframes are defined globally in src/App.css (the `--pls-*`
 * variables and `pls-*` keyframes), so this honours light/dark themes and the
 * reduced-motion kill switch automatically.
 *
 * Props (all optional, backwards compatible with the previous component):
 *  - label:    override the rotating status message with a fixed string.
 *  - progress: controlled 0–100 percentage. When omitted, an indeterminate
 *              "climbing" progress is simulated for visual feedback.
 */
export const FullPageLoading = ({ label = undefined, progress = undefined } = {}) => {
  const { t } = useTranslation();

  const isControlled = typeof progress === "number";
  const [simProgress, setSimProgress] = useState(8);

  // Simulate a climbing progress when no real value is provided. We ease toward
  // 90% and hold, so the bar always feels alive without ever claiming "done".
  useEffect(() => {
    if (isControlled) return undefined;
    const id = setInterval(() => {
      setSimProgress((prev) => {
        if (prev >= 90) return prev;
        const step = prev < 55 ? 6 + Math.random() * 8 : 2 + Math.random() * 4;
        return Math.min(90, prev + step);
      });
    }, 360);
    return () => clearInterval(id);
  }, [isControlled]);

  const pct = Math.max(
    0,
    Math.min(100, Math.round(isControlled ? progress : simProgress))
  );

  // Rotate the status message with progress (matches the doc's recommendation).
  const messageKeys = [
    "loading.boot.initializing",
    "loading.boot.syncing",
    "loading.boot.linking",
    "loading.boot.finalizing",
  ];
  const messageIndex =
    pct < 25 ? 0 : pct < 55 ? 1 : pct < 85 ? 2 : 3;
  const statusMessage = label
    ? label
    : t(messageKeys[messageIndex]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        display: "grid",
        placeItems: "center",
        background: "var(--pls-surface, #FFFFFF)",
        color: "var(--pls-fg, #0A1432)",
      }}
    >
      {/* grid overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(var(--pls-grid, rgba(12,28,70,.05)) 1px,transparent 1px),linear-gradient(90deg,var(--pls-grid, rgba(12,28,70,.05)) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* ambient glow */}
      <div
        className="pls-anim"
        style={{
          position: "absolute",
          width: "min(680px, 90vw)",
          height: "min(680px, 90vw)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle,color-mix(in srgb,var(--pls-accent, #1463C7) 26%,transparent),transparent 62%)",
          filter: "blur(8px)",
          animation: "pls-glowpulse 3.4s ease-in-out infinite",
        }}
      />

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "0 24px",
        }}
      >
        {/* animated mark */}
        <div
          style={{
            position: "relative",
            width: 128,
            height: 128,
            display: "grid",
            placeItems: "center",
            marginBottom: 34,
          }}
        >
          <div
            className="pls-anim"
            style={{
              position: "absolute",
              width: 128,
              height: 128,
              borderRadius: "50%",
              border: "1px dashed var(--pls-border, #E2E7F2)",
              animation: "pls-spinrev 14s linear infinite",
            }}
          />
          <div
            className="pls-anim"
            style={{
              position: "absolute",
              width: 96,
              height: 96,
              borderRadius: "50%",
              background:
                "conic-gradient(from 0deg,transparent 0deg,var(--pls-accent, #1463C7) 300deg,var(--pls-glow, #35D6FF))",
              WebkitMask:
                "radial-gradient(farthest-side,transparent calc(100% - 4px),#000 0)",
              mask: "radial-gradient(farthest-side,transparent calc(100% - 4px),#000 0)",
              animation: "pls-spin 1.05s linear infinite",
              filter:
                "drop-shadow(0 0 10px color-mix(in srgb,var(--pls-accent, #1463C7) 60%,transparent))",
            }}
          />
          <div
            className="pls-anim"
            style={{
              width: 60,
              height: 60,
              borderRadius: 17,
              background:
                "linear-gradient(135deg,var(--pls-accent, #1463C7),var(--pls-accent2, #2D8BE8))",
              display: "grid",
              placeItems: "center",
              animation: "pls-floaty 2.6s ease-in-out infinite",
              boxShadow:
                "0 10px 26px color-mix(in srgb,var(--pls-accent, #1463C7) 50%,transparent)",
            }}
          >
            <div
              className="pls-anim"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "3px solid rgba(255,255,255,.95)",
                borderTopColor: "transparent",
                borderRightColor: "transparent",
                animation: "pls-spin 1.05s linear infinite",
              }}
            />
          </div>
        </div>

        <div
          style={{
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: ".34em",
            marginBottom: 8,
          }}
        >
          PROMANG
        </div>
        <div
          style={{
            fontFamily: "ui-monospace,monospace",
            fontSize: 13,
            color: "var(--pls-muted, #6A7595)",
            minHeight: 18,
            marginBottom: 26,
          }}
        >
          {statusMessage}
        </div>

        {/* progress */}
        <div style={{ width: "min(300px, 80vw)" }}>
          <div
            style={{
              height: 5,
              borderRadius: 99,
              background: "var(--pls-surface2, #F4F6FC)",
              border: "1px solid var(--pls-border, #E2E7F2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 99,
                background:
                  "linear-gradient(90deg,var(--pls-accent, #1463C7),var(--pls-glow, #35D6FF))",
                boxShadow:
                  "0 0 12px color-mix(in srgb,var(--pls-accent, #1463C7) 70%,transparent)",
                width: `${pct}%`,
                transition: "width .35s ease",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
              fontFamily: "ui-monospace,monospace",
              fontSize: 11,
              color: "var(--pls-faint, #9AA6C2)",
            }}
          >
            <span>{t("loading.boot.secure_session")}</span>
            <span>{pct}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};
